import React from "react";
import type { Box } from "./frame";
import type { Theme } from "./theme";

/**
 * Anivaze v4 — TerminalActor: a real Linux terminal that types.
 *
 * Commands ("$ …") type character-by-character at human pace; output lines
 * print after their command lands — exactly how a person demos `kubectl` in a
 * screencast. Pure function of `t` (deterministic schedule derived from the
 * lines), so preview === export. Powers the Nerd pack and any "show me the
 * commands" beat (kubectl, git, docker, bash…).
 */

const MONO = "'Cascadia Code','SF Mono',Consolas,'Courier New',monospace";
const TYPE_CPS = 24;      // chars/sec while "typing" a command
const CMD_GAP = 0.35;     // pause before a command starts typing
const OUT_GAP = 0.18;     // pause before an output line prints
const OUT_STEP = 0.10;    // stagger between consecutive output lines

interface Line {
  text: string;
  isCmd: boolean;
  start: number;  // when this line begins appearing
  end: number;    // when fully visible
}

/** Deterministic reveal schedule for the terminal lines. */
export function scheduleLines(items: string[]): Line[] {
  const lines: Line[] = [];
  let cursor = 0.4; // small settle before the first keystroke
  for (const raw of items) {
    const text = String(raw ?? "").trim();
    if (!text) continue;
    const isCmd = /^[$#>]/.test(text);
    if (isCmd) {
      const start = cursor + CMD_GAP;
      const end = start + Math.max(0.3, text.length / TYPE_CPS);
      lines.push({ text, isCmd, start, end });
      cursor = end;
    } else {
      const start = cursor + (lines.length && lines[lines.length - 1].isCmd ? OUT_GAP : OUT_STEP);
      lines.push({ text, isCmd, start, end: start + 0.05 });
      cursor = start + 0.05;
    }
  }
  return lines;
}

/** Visible portion of a line at time t ("" = not started, full = done). */
export function lineTextAt(line: Line, t: number): string {
  if (t < line.start) return "";
  if (!line.isCmd || t >= line.end) return line.text;
  const frac = (t - line.start) / (line.end - line.start);
  return line.text.slice(0, Math.max(1, Math.floor(frac * line.text.length)));
}

interface VProps { items: string[]; box: Box; theme: Theme; unit: number; t: number }

export const TerminalActor: React.FC<VProps> = ({ items, box, theme, unit, t }) => {
  const lines = scheduleLines(items.length ? items : ["$ echo hello", "hello"]);
  const pad = unit * 2.2;
  const w = Math.min(box.w, unit * 86);
  const x = box.cx - w / 2;
  const titleH = unit * 3.4;
  const lineH = unit * 3.0;
  const bodyH = Math.min(box.h - titleH - unit * 2, pad * 2 + lines.length * lineH);
  const y = box.cy - (titleH + bodyH) / 2;
  const fontSize = unit * 1.95;
  const maxChars = Math.floor((w - pad * 2) / (fontSize * 0.62));
  const activeIdx = lines.findIndex((l) => t >= l.start && t < l.end && l.isCmd);
  const cursorOn = Math.floor(t * 2.4) % 2 === 0;

  return (
    <g>
      {/* window */}
      <rect x={x} y={y} width={w} height={titleH + bodyH} rx={unit * 1.2} fill="#10141a" stroke={theme.palette.primary} strokeOpacity={0.35} strokeWidth={1.5} />
      {/* title bar */}
      <rect x={x} y={y} width={w} height={titleH} rx={unit * 1.2} fill="#1a212b" />
      <rect x={x} y={y + titleH - unit * 0.6} width={w} height={unit * 0.6} fill="#1a212b" />
      {["#ff5f57", "#febc2e", "#28c840"].map((c, i) => (
        <circle key={c} cx={x + pad + i * unit * 2} cy={y + titleH / 2} r={unit * 0.55} fill={c} />
      ))}
      <text x={x + w / 2} y={y + titleH / 2} textAnchor="middle" dominantBaseline="central" fontFamily={MONO} fontSize={unit * 1.5} fill="#8a93a0">
        bash — terminal
      </text>
      {/* lines */}
      {lines.map((l, i) => {
        const shown = lineTextAt(l, t);
        if (!shown) return null;
        const display = shown.length > maxChars ? shown.slice(0, maxChars - 1) + "…" : shown;
        const ly = y + titleH + pad + i * lineH + lineH / 2;
        if (ly > y + titleH + bodyH - pad / 2) return null;
        return (
          <text key={i} x={x + pad} y={ly} dominantBaseline="central" fontFamily={MONO} fontSize={fontSize}
            fill={l.isCmd ? "#e8edf3" : "#7ee88b"} opacity={l.isCmd ? 1 : 0.92}>
            {l.isCmd && <tspan fill={theme.palette.primary}>{display.slice(0, 1)}</tspan>}
            {l.isCmd ? display.slice(1) : display}
            {i === activeIdx && cursorOn && <tspan fill="#e8edf3">▌</tspan>}
          </text>
        );
      })}
    </g>
  );
};
